import { fetchJSON as fetchAPI } from './client';

export type CustomerGrowthDashboard = {
  tenant_id: string;
  total_reviews: number;
  avg_rating: number;
  pending_questions: number;
  active_vine_enrollments: number;
  brand_followers: number;
  active_campaigns: number;
  avg_customer_health: number;
  avg_review_health: number;
  avg_engagement_score: number;
  active_recommendations: number;
  generated_at?: string;
};

export type ReviewRecord = {
  id: string;
  asin?: string;
  sku?: string;
  title?: string;
  body?: string;
  rating: number;
  status: string;
  sentiment?: string;
  language?: string;
  verified: boolean;
  helpful_votes: number;
  review_date?: string;
};

export type RatingRecord = {
  id: string;
  asin?: string;
  sku?: string;
  avg_rating: number;
  total_reviews: number;
  five_star_pct: number;
  one_star_pct: number;
  review_velocity: number;
};

export type QuestionRecord = {
  id: string;
  asin?: string;
  sku?: string;
  question: string;
  status: string;
  answer_count: number;
  suggested_answer?: string;
  asked_at?: string;
};

export type VineEnrollmentRecord = {
  id: string;
  asin?: string;
  sku?: string;
  product_name?: string;
  status: string;
  reviews_target: number;
  reviews_received: number;
  completion_pct: number;
  estimated_roi?: number;
  enrolled_at?: string;
};

export type FollowerRecord = {
  id: string;
  brand_name?: string;
  follower_count: number;
  growth_rate: number;
  engagement_rate: number;
};

export type EngagementCampaignRecord = {
  id: string;
  name: string;
  type?: string;
  status: string;
  audience_size: number;
  open_rate: number;
  click_rate: number;
  conversion_rate: number;
  revenue_attribution?: number;
};

export type CustomerHealthRecord = {
  id: string;
  asin?: string;
  sku?: string;
  product_name?: string;
  customer_health_score: number;
  loyalty_score: number;
  review_health_score: number;
  engagement_score: number;
  trust_score: number;
  repeat_purchase_score: number;
  issue?: string;
  severity?: string;
};

export type GrowthRecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  asin?: string;
  sku?: string;
  expected_impact?: string;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
  source_module?: string;
};

const API_BASE = import.meta.env.VITE_CUSTOMERGROWTH_API ?? '/api/v1/customergrowth';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const customergrowthApi = {
  dashboard: () => fetchJSON<CustomerGrowthDashboard>('/dashboard'),
  listReviews: () => fetchJSON<ReviewRecord[]>('/reviews'),
  listRatings: () => fetchJSON<RatingRecord[]>('/ratings'),
  listQuestions: () => fetchJSON<QuestionRecord[]>('/questions'),
  listVine: () => fetchJSON<VineEnrollmentRecord[]>('/vine'),
  listFollowers: () => fetchJSON<FollowerRecord[]>('/followers'),
  listCampaigns: () => fetchJSON<EngagementCampaignRecord[]>('/campaigns'),
  listHealth: () => fetchJSON<CustomerHealthRecord[]>('/health'),
  listRecommendations: () => fetchJSON<GrowthRecommendation[]>('/recommendations'),
  generateRecommendations: () =>
    fetchJSON<GrowthRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  listSyncJobs: () => fetchJSON<{ id: string; mode: string; status: string }[]>('/sync-jobs'),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
